import { useCallback, useEffect, useMemo, useRef } from "react";

import { runInAction } from "mobx";
import { useEnvironment } from "Shared/Hooks";
import { useORPCQuery } from "Core/oRPC";
import { useQuery } from "@tanstack/react-query";
import { useRootStore } from "Store/RootStore";
import { utcDateTime } from "Shared/Hooks/Time/useTimeZone";

export const useMyManagers = () => {
    const myStore = useRootStore("MyStore");
    const { shasApi } = useORPCQuery();
    const { isPublic } = useEnvironment();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const updateManagers = useCallback(
        (managers: string[]) => {
            runInAction(() => {
                myStore.managers = managers;
                const newExpireTime = utcDateTime().plus({ days: 1 }).toJSDate();
                myStore.managersExpireTime = newExpireTime;
            });
        },
        [myStore]
    );

    const shouldFetchManagers = useMemo(() => {
        const now = utcDateTime().toJSDate();
        return myStore.managersExpireTime < now;
    }, [myStore.managersExpireTime]);

    const {
        data: shasView,
        status,
        refetch
    } = useQuery(
        shasApi.getViewalias.queryOptions({
            input: { alias: myStore.profile.Alias ?? "" },
            staleTime: 6 * 60 * 60 * 1000,
            enabled: !!myStore.profile.Alias && shouldFetchManagers && isPublic
        })
    );

    useEffect(() => {
        if (status === "success") {
            updateManagers(shasView.Managers || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, shasView, updateManagers]);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(
            async () => {
                const now = utcDateTime().toJSDate();
                if (myStore.managersExpireTime <= now) {
                    const result = await refetch();

                    if (result.data) {
                        updateManagers(result.data.Managers || []);
                    }
                }
            },
            12 * 60 * 60 * 1000
        );

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [myStore, refetch, updateManagers]);
};
